import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Download, Info, MapPin, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useSiteData } from "@/context/SiteDataContext";
import { API, api, inr, waLink } from "@/lib/api";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { ItineraryTimeline } from "@/components/site/ItineraryTimeline";
import { StayFerryDetails } from "@/components/site/StayFerryDetails";
import { EnquiryDialog } from "@/components/site/EnquiryDialog";
import { MobileCtaBar } from "@/components/site/MobileCtaBar";
import { SITE_URL, Seo, breadcrumb } from "@/components/site/Seo";

const packageSeo = (pkg) => {
  const path = `/packages/${pkg.slug}`;
  const places = [...new Set(pkg.itinerary.map((d) => d.destination).filter(Boolean))];
  const description = `${pkg.name}: ${pkg.duration} ${pkg.category} Andaman tour package covering ${places.join(", ") || pkg.highlights.join(", ")}. Day-wise itinerary, hotel stay, ferry and transfers. ${pkg.tagline}`.slice(0, 300);
  const trip = {
    "@context": "https://schema.org", "@type": "TouristTrip", name: pkg.name, description, image: pkg.image, url: `${SITE_URL}${path}`,
    touristType: [pkg.category, ...(pkg.alt_categories || [])],
    provider: { "@type": "TravelAgency", "@id": `${SITE_URL}/#agency`, name: "Ocean Wings Travel", url: `${SITE_URL}/` },
    itinerary: { "@type": "ItemList", numberOfItems: pkg.itinerary.length, itemListElement: pkg.itinerary.map((d, i) => ({ "@type": "ListItem", position: i + 1, name: `Day ${d.day}: ${d.title}`, description: d.description })) },
    offers: { "@type": "Offer", price: Math.round(pkg.selling_price), priceCurrency: "INR", availability: "https://schema.org/InStock", url: `${SITE_URL}${path}`, description: "Starting price per person, twin sharing" },
  };
  return { path, description, jsonLd: [trip, breadcrumb([["Home", "/"], ["Tour Packages", "/packages"], [pkg.name, path]])] };
};

const List = ({ title, items, ok }) => (
  <div className="rounded-2xl border border-slate-200 p-6">
    <p className="font-semibold text-navy">{title}</p>
    <ul className="mt-4 space-y-3">
      {items.map((i) => (
        <li key={i} className="flex gap-3 text-sm text-slate-600">
          {ok ? <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> : <X className="h-4 w-4 text-coral shrink-0 mt-0.5" />}{i}
        </li>
      ))}
    </ul>
  </div>
);

export default function PackageDetail() {
  const { slug } = useParams();
  const { settings } = useSiteData();
  const [pkg, setPkg] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPkg(null);
    setError(false);
    api.get(`/packages/${slug}`).then((r) => setPkg(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error) return (
    <div className="container-x py-24 text-center" data-testid="package-not-found">
      <h1 className="font-display text-4xl text-navy">Package not found</h1>
      <Link to="/packages" className="mt-6 inline-block text-ocean font-semibold">Browse all packages</Link>
    </div>
  );
  if (!pkg) return <div className="container-x py-12"><Skeleton className="h-[420px] rounded-3xl" /></div>;

  const wa = waLink(settings.whatsapp, `Hi Ocean Wings Travel, I'd like to book "${pkg.name}" (${pkg.duration}). Please share availability and final price.`);

  return (
    <div data-testid="package-detail-page">
      <Seo title={`${pkg.name} Andaman Package | ${pkg.category} | Ocean Wings Travel`} image={pkg.image} {...packageSeo(pkg)} />
      <section className="relative h-[420px] md:h-[520px] overflow-hidden">
        <img src={pkg.image} alt={`${pkg.name} - ${pkg.duration} Andaman tour package`} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/90 via-[#0A192F]/30 to-transparent" />
        <div className="relative container-x h-full flex flex-col justify-end pb-10">
          <Link to="/packages" className="text-sm text-slate-200 hover:text-white inline-flex items-center gap-1 mb-auto mt-8" data-testid="package-back-link"><ArrowLeft className="h-4 w-4" /> All packages</Link>
          <div className="flex gap-2">
            <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-navy">{[pkg.category, ...(pkg.alt_categories || [])].join(" · ")}</span>
            <span className="rounded-full bg-ocean px-3 py-1 font-mono text-xs font-semibold text-white">{pkg.duration}</span>
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-white font-semibold" data-testid="package-title">{pkg.name}</h1>
          <p className="mt-3 text-slate-200 max-w-2xl">{pkg.tagline}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <EnquiryDialog packageId={pkg.id} title={`Free quote: ${pkg.name}`} idPrefix="hero-enquiry"
              trigger={<button type="button" data-testid="package-hero-quote-button" className="h-12 px-6 rounded-full bg-white text-navy font-semibold hover:bg-sky-50 transition-colors">Get Free Quote</button>} />
            <a href={wa} target="_blank" rel="noreferrer" data-testid="package-hero-whatsapp-button"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors"><WhatsAppIcon className="h-5 w-5" /> WhatsApp Enquiry</a>
          </div>
        </div>
      </section>

      <section className="container-x py-14 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-8 space-y-12">
          {pkg.route?.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="package-route">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Package route</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-2">
                {pkg.route.map((r, i) => (
                  <span key={`${r}-${i}`} className="inline-flex items-center gap-1.5">
                    {i > 0 && <ArrowRight className="h-3.5 w-3.5 text-sky-300" />}
                    <span className="rounded-full bg-sky-50 px-3 py-1 text-sm font-medium text-navy">{r}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {pkg.highlights.map((h) => <span key={h} className="inline-flex items-center gap-1.5 rounded-full bg-mist border border-sky-100 px-3 py-1.5 text-sm text-slate-700"><MapPin className="h-3.5 w-3.5 text-ocean" />{h}</span>)}
          </div>
          <div data-testid="package-itinerary-section">
            <p className="eyebrow">Day-wise itinerary</p>
            <h2 className="mt-2 font-display text-4xl text-navy font-semibold">Your {pkg.days}-day journey</h2>
            <p className="mt-2 mb-8 text-sm text-slate-500">{pkg.nights} nights · {pkg.days} days · day-by-day plan with sightseeing, transfers, activities and hotel stay.</p>
            <ItineraryTimeline days={pkg.itinerary} />
          </div>
          <StayFerryDetails days={pkg.itinerary} />
          <div className="grid gap-6 md:grid-cols-2">
            <List title="Inclusions" items={pkg.inclusions} ok />
            <List title="Exclusions" items={pkg.exclusions} />
          </div>
          {pkg.notes?.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-6" data-testid="package-notes">
              <p className="flex items-center gap-2 font-semibold text-amber-900"><Info className="h-4 w-4" />Please note</p>
              <ul className="mt-3 space-y-2 text-sm text-amber-900/90 list-disc pl-5">{pkg.notes.map((n) => <li key={n}>{n}</li>)}</ul>
            </div>
          )}
          <div id="package-enquiry" className="rounded-3xl border border-slate-200 p-6 sm:p-8">
            <h2 className="font-display text-3xl text-navy font-semibold">Get a free quote for this package</h2>
            <p className="mt-1 mb-6 text-sm text-slate-600">Tell us your dates and group size. We'll reply with a personalised quotation.</p>
            <EnquiryForm defaultPackageId={pkg.id} idPrefix="package-enquiry" />
          </div>
        </div>
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="package-price-card">
            <p className="text-xs uppercase tracking-wider text-slate-500">Starting from</p>
            <p className="mt-1 font-mono text-4xl font-semibold text-ocean" data-testid="package-price">{inr(pkg.selling_price)}</p>
            <p className="text-sm text-slate-500">per person, twin sharing</p>
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">Indicative price. Final quote depends on travel dates, hotel availability and group size.</p>
            <div className="mt-6 flex flex-col gap-3">
              <a href={wa} target="_blank" rel="noreferrer" data-testid="package-whatsapp-book-button"
                className="inline-flex items-center justify-center gap-2 h-12 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors">
                <WhatsAppIcon className="h-5 w-5" /> WhatsApp Enquiry
              </a>
              <a href="#package-enquiry" data-testid="package-enquire-button"
                className="inline-flex items-center justify-center h-12 rounded-full bg-ocean text-white font-semibold hover:bg-navy transition-colors">
                Get Free Quote
              </a>
              <a href={`${API}/packages/${pkg.slug}/itinerary.pdf`} data-testid="package-download-itinerary-button"
                className="inline-flex items-center justify-center gap-2 h-12 rounded-full border border-slate-200 text-navy font-semibold hover:border-ocean hover:text-ocean transition-colors">
                <Download className="h-4 w-4" /> Download Itinerary PDF
              </a>
            </div>
          </div>
        </aside>
      </section>
      <MobileCtaBar packageId={pkg.id} waText={`Hi Ocean Wings Travel, I'd like to book "${pkg.name}" (${pkg.duration}). Please share availability and final price.`} prefix="package-mobile" />
    </div>
  );
}
